import { FC, useEffect, useRef, useState } from 'react';
import OrderBook from './OrderBook';
import BookSummary from './Summary';
import FlightBookSkeleton from './FlightBookSkeleton';
import useFlightBook from 'hooks/useFlightBook';
import { CmsPageResponse } from 'ts/shared/interfaces/CmsPageResponse';
import ExpiredToken from 'components/shared/ExpiredToken';
import usePageInMemory from 'hooks/usePageInMemory';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { useGetPage } from 'hooks/useGetPage';
import { saveScreenshot } from 'utils/utils';
import { getAnyPage, updatePaymentBlock } from 'services/flight';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Pax } from 'ts/flight/interfaces/FlightBookResponse';
import { ListOFCatalogPrice } from 'ts/flight/interfaces/FlightDetailsResponse';
import Button from 'components/ui/Button/Button';
import { faArrowCircleLeft, faSearch } from '@fortawesome/free-solid-svg-icons';
import { isNetravel } from 'constants/generic';

const FlightBookPage: FC = () => {
  const { response, bounds, setResponse } = useFlightBook();
  const bookRef = useRef<HTMLDivElement>(null);
  
  const [couponCode, setCouponCode] = useState('');
  const [addPrice, setAddPrice] = useState(0);
  const { t, i18n } = useTranslation();
  const [paxes, setPaxes] = useState<Pax[]>([]);
  const [netravelPage, setNetravelPage] = useState<CmsPageResponse>();
  const [urlParams] = useSearchParams();
  const location = useLocation();
  const idbKey = location.pathname + location.search;
  const { onClickBack, onClickSearch } = usePageInMemory(idbKey, () => {});

  useGetPage('Flights/Book');

  useEffect(() => {
    (async () => {
      if (isNetravel) {
        const netravelPage = await getAnyPage({
          products: 'Flights',
          category: 'SystemComplex',
          systemProductsDetails: 'Booking',
        });
        setNetravelPage(netravelPage);
      }
    })();
  }, []);

  const onClickApply = async () => {
    const res = await updatePaymentBlock({
      packageCurrency: response?.bookingModel?.currency || '',
      sourcePrice: response?.bookingModel?.credit2000PaymentInfo?.fullPriceInShekel || 0,
      addPrice: parseInt(couponCode || '0') + addPrice,
      searchToken: urlParams.get('searchToken') || '',
      lang: i18n.language,
    });

    if (!response?.bookingModel || !res) return;
    setResponse({
      ...response,
      bookingModel: {
        ...response.bookingModel,
        credit2000PaymentInfo: res.credit2000PaymentInfo,
      },
    });
  };

  const onSubmit = async () => {
    await saveScreenshot(bookRef.current, 'Book', 'Flights');
  };

  const getBags = () => {
    const bags = response?.bookingModel?.baggageFlightDetails?.bookFareAndBaggageFlights
      ?.map((fl) => [
        ...(fl.outbound?.listOfCatalogPrice || []),
        ...(fl.inbound?.listOfCatalogPrice || []),
      ])
      .flat();
    const paxesMap = new Map<number, string[]>();
    paxes.forEach((pax, index) => {
      paxesMap.set(index + 1, [
        ...(pax.baggageOutboundArr || []),
        ...(pax.baggageInboundArr || []),
      ]);
    });
    const result = Array.from(paxesMap.entries()).flatMap(([paxCode, bagIds]) => {
      return bagIds
        .map((id) => {
          const bag = bags?.find((bag) => bag.code === id);
          return bag ? { ...bag, paxCode } : null;
        })
        .filter((bag): bag is ListOFCatalogPrice & { paxCode: number } => bag !== null);
    });
    return result;
  };
  return (
      <div className="details-wrapper" ref={bookRef}>
        {!!response?.bookingModel?.flight && (
          <div className="flex-column">
            <div className="mb-20 default-shadow flex-between dyn-change-header">
              <Button
                title={t('back')}
                icon={faArrowCircleLeft}
                onClick={() => onClickBack('flight', 'details')}
                className="btn btn-link"
              />
              <Button
                title={t('search.new')}
                icon={faSearch}
                onClick={() => onClickSearch('flight', '/flights')}
                className="btn btn-link"
              />
            </div>
            <div className="flex-between w100p">
              <OrderBook
                isMulti={false}
                bookingModel={response.bookingModel}
                restrictOptionalService={response.restrictOptionalService}
                restrictOptionalServicePair={true}
                privileges={response.privilegesInfo}
                onSubmit={onSubmit}
                couponCode={couponCode}
                setAddPrice={setAddPrice}
                addPrice={addPrice}
                netravelPage={netravelPage}
                onUpdateCredit={(cred) =>
                  setResponse({
                    ...response,
                    bookingModel: { ...response.bookingModel!, credit2000PaymentInfo: cred },
                  })
                }
                paxes={paxes}
                setPaxes={setPaxes}
              />
              <BookSummary
                flight={response?.bookingModel.flight}
                bounds={bounds}
                couponCode={couponCode}
                setCouponCode={setCouponCode}
                onClickApply={onClickApply}
                addPrice={addPrice}
                bags={getBags()}
                netravelPage={netravelPage}
              />
            </div>
          </div>
        )}

        {response?.sessionExpired && <ExpiredToken storePath="flight" />}

        {!response && <FlightBookSkeleton />}

        {response?.hasErrors && (
          <div className="expired">
            <div className="title">{t('something_went_wrong')}</div>
            <p className="expired-content">Flight was not found</p>
            <Link to={'/'} className="btn btn-primary">
              {t('home.go_back')}
            </Link>
          </div>
        )}
      </div>
  );
};

export default withErrorBoundary(FlightBookPage, 'FlightBookPage');
